import { useLingui } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import IconPackage from '@/icons/Package.svg';
import IconPlace from '@/icons/Place.svg';
import IconTruck from '@/icons/Truck.svg';
import { getDestination, getOrigin, ShipmentWithRelations } from '@/models/shipment';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import './ShipmentOverview.css';

const { block, element } = createBEMClasses('shipment-overview');

export const ShipmentOverview = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { isMobile } = useViewPort();

  const origin = getOrigin(shipment);
  const destination = getDestination(shipment);

  return (
    <Stack className={block()}>
      <Stack row gap="0.25rem" alignItems="center" justifyContent="stretch">
        {!isMobile && <IconPlace start style={{ fontSize: '1.25rem' }} />}
        <Stack row style={{ flex: 1 }} alignItems="center" gap="0.5rem">
          <LocationName name={origin?.name} origin />
          <Stack row style={{ flex: 1 }} gap="0.5rem" alignItems="center">
            {shipment.step === 'handover' && <IconPackage style={{ fontSize: '1.5rem' }} />}
            <div
              className={element('transit', {
                completed:
                  shipment.step === 'in-transit' ||
                  shipment.step === 'verification' ||
                  shipment.step === 'validated',
              })}
            />
            {shipment.step === 'in-transit' && (
              <>
                <IconTruck style={{ fontSize: '1.5rem' }} />
                <div className={element('transit')} />
              </>
            )}
            {shipment.step === 'verification' && <IconPackage style={{ fontSize: '1.5rem' }} />}
          </Stack>
          <LocationName name={destination?.name} />
        </Stack>
        {!isMobile && <IconPlace end style={{ fontSize: '1.25rem' }} />}
      </Stack>
      <Stack
        style={{
          padding: isMobile ? undefined : '0 1.5rem',
        }}
        row
        justifyContent="space-between"
      >
        <LocationCity city={origin?.address?.city} state={origin?.address?.state} />
        <LocationCity city={destination?.address?.city} state={destination?.address?.state} />
      </Stack>
    </Stack>
  );
};

const LocationName = ({ name, origin }: { name?: string | null; origin?: boolean }) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  if (!name) {
    name = origin
      ? t({ id: 'shipment.origin.title', message: 'Origin' })
      : t({ id: 'shipment.destination.title', message: 'Destination' });
  }

  return (
    <Title
      size={isMobile ? '500' : '400'}
      as="h3"
      className={element('location-name')}
      title={name}
      ellipsis
    >
      {name}
    </Title>
  );
};

const LocationCity = ({ city, state }: { city?: string | null; state?: string | null }) => {
  const cityState = [city, state].filter((el) => !!el).join(', ');

  if (!cityState) {
    return null;
  }

  return (
    <InlineText size="200" ellipsis className={element('location-city', {})} title={cityState}>
      {cityState}
    </InlineText>
  );
};
